import { ERROR_CODE } from '@myre/app-user-domain/_reserved-error-codes';
import { checkDate } from '@myre/common-dates';
import {
  checkTwoFaLimit,
  TWO_FA_LIMIT,
  TWO_FA_LIMIT_RESET,
  TWO_FA_MARGIN_MINUTES,
} from '@myre/common-permission';
import { DynamicEnvService } from '@myre/ui-domain-http';
import { LANGLOCALE } from '@myre/ui-primitive-core';

import {
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  type HttpEvent,
  type HttpInterceptor,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { mergeMap, Observable, of, takeUntil, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { takeFirst } from '../helpers/observable.helpers';
import { BaseAuthService } from '../services/auth/auth.service';

import { AbstractInterceptor } from './astract.interceptor';

@Injectable({ providedIn: 'root' })
export class CredentialsInterceptor extends AbstractInterceptor {
  private readonly _langLocale = inject(LANGLOCALE);
  private readonly _baseAuthService = inject(BaseAuthService);

  protected override _handle(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const setHeaders: Record<string, string | string[]> = {};

    if (typeof this._langLocale === 'string') {
      setHeaders.Lang = this._langLocale;
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (typeof tz === 'string') {
      setHeaders['Time-Zone'] = tz;
    }

    // withCredentials to ensure that Authorization header and cookies are sent
    return next
      .handle(
        req.clone({
          setHeaders,
          withCredentials: true,
        }),
      )
      .pipe(
        catchError(event => {
          if (
            event instanceof HttpErrorResponse &&
            event.status === 401 &&
            // if we are on user, we don't want to redirect
            !!this._baseAuthService.myreApp
          ) {
            this._baseAuthService.onLogout();

            return this._baseAuthService
              .navigateToLogin()
              .pipe(mergeMap(() => throwError(() => event)));
          }

          return throwError(() => event);
        }),
      );
  }
}

@Injectable({ providedIn: 'root' })
export class MfaInterceptor implements HttpInterceptor {
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private _baseAuthService = inject(BaseAuthService);
  private _httpClient = inject(HttpClient);

  private _observable$: Observable<boolean>;

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const re = new RegExp(
      `${this._dynamicEnvService.env.domain}/customers/(\\d+)`,
    );
    const match = req.url.match(re);

    if (match) {
      const customerId = match && +match[1];
      const customer = this._baseAuthService.customers.find(
        c => c.id === customerId,
      );

      if (
        customer?.twoFactorAuthentication &&
        !checkTwoFaLimit(
          this._baseAuthService.twoFaUntil,
          TWO_FA_MARGIN_MINUTES,
        )
      ) {
        this._observable$ ||= this._baseAuthService.openCheck2faModal(customer);

        return this._proxy(req, next);
      }
    }

    return this._handle(req, next);
  }

  private _proxy(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this._observable$.pipe(
      takeFirst(),
      mergeMap(res => {
        if (!res) {
          // cool down of 1s before accepting new requests from this customer
          setTimeout(() => {
            this._observable$ = null;
          }, 1000);

          return of(new HttpResponse({ status: 401, body: 'Unauthorized' }));
        }

        this._observable$ = null;

        return this._handle(req, next);
      }),
    );
  }

  private _handle(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      takeUntil(this._baseAuthService.onCancelPendingRequests()),
      tap(event => {
        if (event instanceof HttpResponse) {
          const twoFaLimitHeader = event.headers.get(TWO_FA_LIMIT);

          if (twoFaLimitHeader) {
            this._baseAuthService.twoFaUntil =
              twoFaLimitHeader === TWO_FA_LIMIT_RESET
                ? null
                : checkDate(event.headers.get(TWO_FA_LIMIT));
          }
        }
      }),
      catchError(event => {
        if (
          event instanceof HttpErrorResponse && // in case a request was sent but 2FA is not valid, force until = null and retry
          // this will trigger the modal during new request
          event.error?.code === ERROR_CODE.TwoFactorAuthenticationRequiredError
        ) {
          this._baseAuthService.twoFaUntil = null;

          return this._httpClient.request(req.clone());
        }

        return throwError(() => event);
      }),
    );
  }
}
